import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Table } from 'reactstrap';
import toast from 'react-hot-toast';
import { walletService, customerService } from '../services/api';
import * as Icons from 'lucide-react';
import { formatCurrencyCOP } from '../utils/formatters';

/**
 * Multi-Invoice RC Dialog Component
 * 
 * Allows creating a single Cash Receipt (RC) in Siigo that applies
 * a single payment to multiple orders/invoices from the same customer.
 * 
 * Props:
 * - isOpen: boolean - Controls dialog visibility
 * - toggle: function - Function to close the dialog
 * - onSuccess: function - Callback after successful RC creation
 */
const MultiInvoiceRCDialog = ({ isOpen, toggle, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Customer Selection, 2: Order Selection & Payment, 3: Confirm
    const [loading, setLoading] = useState(false);

    // Step 1: Customer selection
    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Step 2: Order selection and payment distribution
    const [pendingOrders, setPendingOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState(new Map()); // Map<orderId, paymentAmount>
    const [paymentMethod, setPaymentMethod] = useState('transferencia');
    const [siigoPaymentId, setSiigoPaymentId] = useState('7476'); // Default: Consignación Bancaria
    const [notes, setNotes] = useState('');

    // Bank reconciliation
    const [linkedMovements, setLinkedMovements] = useState([]); // Array of full movement objects
    const [movementSearchQuery, setMovementSearchQuery] = useState('');
    const [foundMovements, setFoundMovements] = useState([]); // Results from search
    const [loadingMovements, setLoadingMovements] = useState(false);

    // File Upload
    const [files, setFiles] = useState([]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setCustomerSearch('');
            setCustomers([]);
            setSelectedCustomer(null);
            setPendingOrders([]);
            setSelectedOrders(new Map());
            setPaymentMethod('transferencia');
            setSiigoPaymentId('7476');
            setNotes('');
            setLinkedMovements([]);
            setMovementSearchQuery('');
            setFoundMovements([]);
            setFoundMovements([]);
            setFiles([]);
        }
    }, [isOpen]);

    // Handle file selection
    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setFiles(prev => [...prev, ...newFiles].slice(0, 5));
        }
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Handle Ctrl+V paste for images
    const handlePaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const imageFiles = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                    const timestamp = Date.now();
                    const ext = file.type.split('/')[1] || 'png';
                    const renamedFile = new File([file], `comprobante_${timestamp}.${ext}`, { type: file.type });
                    imageFiles.push(renamedFile);
                }
            }
        }
        if (imageFiles.length > 0) {
            e.preventDefault();
            setFiles(prev => [...prev, ...imageFiles].slice(0, 5));
        }
    }, []);

    // Drag & drop state
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files?.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            setFiles(prev => [...prev, ...newFiles].slice(0, 5));
        }
    };

    // Search customers
    const handleCustomerSearch = async (searchTerm) => {
        setCustomerSearch(searchTerm);

        if (searchTerm.length < 3) {
            setCustomers([]);
            return;
        }

        try {
            const response = await customerService.searchCustomers(searchTerm);
            setCustomers(response.customers || []);
        } catch (error) {
            console.error('Error searching customers:', error);
            toast.error('Error buscando clientes');
        }
    };

    // Select customer and load pending orders
    const handleSelectCustomer = async (customer) => {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.name || customer.commercial_name || 'Cliente');
        setCustomers([]);

        try {
            setLoading(true);
            const response = await walletService.getPendingOrdersForCustomer(customer.id);
            setPendingOrders(response.data || []);

            if (response.data && response.data.length === 0) {
                toast.error('Este cliente no tiene pedidos pendientes de pago');
            } else {
                setStep(2);
            }
        } catch (error) {
            console.error('Error loading pending orders:', error);
            toast.error('Error cargando pedidos pendientes');
        } finally {
            setLoading(false);
        }
    };

    // Toggle order selection
    const handleToggleOrder = (order) => {
        const newMap = new Map(selectedOrders);

        if (newMap.has(order.id)) {
            newMap.delete(order.id);
        } else {
            // Default: pay full balance
            newMap.set(order.id, parseFloat(order.balance));
        }

        setSelectedOrders(newMap);
    };

    // Update payment amount for an order
    const handleUpdatePaymentAmount = (orderId, amount) => {
        const newMap = new Map(selectedOrders);
        newMap.set(orderId, parseFloat(amount) || 0);
        setSelectedOrders(newMap);
    };

    // Calculate total payment
    const calculateTotalPayment = () => {
        return Array.from(selectedOrders.values()).reduce((sum, amt) => sum + amt, 0);
    };

    // Search Bancolombia movements
    const handleSearchMovements = async (query) => {
        setMovementSearchQuery(query);
        if (!query || query.length < 3) {
            setFoundMovements([]);
            return;
        }

        try {
            setLoadingMovements(true);
            // Use the robust endpoint instead of the one causing 404s
            // Filter locally or via backend if supported
            const response = await walletService.getBancolombiaMovements({
                status: 'pending',
                search: query,
                limit: 20
            });

            // Handle different response formats
            let results = [];
            if (Array.isArray(response)) {
                results = response;
            } else if (response.data && Array.isArray(response.data)) {
                results = response.data;
            } else if (response.movements && Array.isArray(response.movements)) {
                results = response.movements;
            }

            // Exclude already linked movements
            const linkedIds = linkedMovements.map(m => m.id);
            setFoundMovements(results.filter(m => !linkedIds.includes(m.id)));
        } catch (error) {
            console.error('Error searching movements:', error);
        } finally {
            setLoadingMovements(false);
        }
    };

    // Link a movement
    const handleLinkMovement = (movement) => {
        if (!linkedMovements.some(m => m.id === movement.id)) {
            setLinkedMovements([...linkedMovements, movement]);
        }
        setMovementSearchQuery(''); // Clear search after selection
        setFoundMovements([]);
    };

    // Unlink a movement
    const handleUnlinkMovement = (movementId) => {
        setLinkedMovements(linkedMovements.filter(m => m.id !== movementId));
    };

    // Create Multi-Invoice RC
    const handleCreateRC = async () => {
        if (selectedOrders.size === 0) {
            toast.error('Selecciona al menos un pedido');
            return;
        }

        if (selectedOrders.size > 10) {
            toast.error('No se pueden procesar más de 10 pedidos en un solo RC');
            return;
        }

        const orderPayments = Array.from(selectedOrders.entries()).map(([orderId, amount]) => ({
            orderId,
            amount
        }));

        const formData = new FormData();
        formData.append('orderPayments', JSON.stringify(orderPayments));
        formData.append('paymentMethod', paymentMethod);
        formData.append('siigoPaymentId', siigoPaymentId);
        formData.append('notes', notes);

        // Append linked movements IDs
        const movementIds = linkedMovements.map(m => m.id);
        if (movementIds.length > 0) {
            formData.append('linkedMovementIds', JSON.stringify(movementIds));
        }

        // Append files
        files.forEach(file => {
            formData.append('files', file);
        });

        try {
            setLoading(true);
            // Check if walletService.createMultiInvoiceRC supports FormData (it usually sends JSON)
            // We might need to ensure api.js sets headers correctly or use a new method
            // Since we updated the backend to handle multipart/form-data, we send formData directly.
            // Axios/Fetch usually detects FormData and sets Content-Type automatically.
            const response = await walletService.createMultiInvoiceRC(formData);

            if (response.success) {
                toast.success(response.message || 'RC Multi-Pedido creado exitosamente');

                // Show link to RC
                if (response.data?.link) {
                    toast.success(
                        <div>
                            <div>{response.data.rcName}</div>
                            <a href={response.data.link} target="_blank" rel="noopener noreferrer" className="underline">
                                Ver RC en Siigo
                            </a>
                        </div>,
                        { duration: 8000 }
                    );
                }

                if (onSuccess) onSuccess(response.data);
                toggle();
            }
        } catch (error) {
            console.error('Error creating multi-invoice RC:', error);
            const errorMsg = error.response?.data?.message || 'Error creando RC multi-pedido';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('es-CO');
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" className="modal-dialog-centered modal-xl-custom">
            <ModalHeader toggle={toggle}>
                RC Multi-Pedido {step === 1 && '- Seleccionar Cliente'}
                {step === 2 && '- Seleccionar Pedidos y Distribuir Pago'}
            </ModalHeader>

            <ModalBody className="p-0">
                {/* Step 1: Customer Selection */}
                {step === 1 && (
                    <div className="p-4">
                        <FormGroup>
                            <Label>Buscar Cliente</Label>
                            <div className="relative">
                                <Icons.Search className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Nombre, NIT o identificación..."
                                    value={customerSearch}
                                    onChange={(e) => handleCustomerSearch(e.target.value)}
                                    autoFocus
                                    className="pl-10"
                                />
                            </div>
                        </FormGroup>

                        {customers.length > 0 && (
                            <div className="border rounded mt-2 max-h-64 overflow-y-auto bg-white shadow-sm">
                                <Table hover size="sm" className="mb-0">
                                    <tbody>
                                        {customers.map((customer) => (
                                            <tr
                                                key={customer.id}
                                                onClick={() => handleSelectCustomer(customer)}
                                                className="cursor-pointer"
                                            >
                                                <td className="p-3">
                                                    <div className="font-bold text-gray-800">
                                                        {customer.name || customer.commercial_name || 'Sin Nombre'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                                        <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                                                            {customer.identification}
                                                        </span>
                                                        {customer.city && (
                                                            <span>• {customer.city}</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {loading && <div className="text-center py-8"><Icons.Loader className="animate-spin mx-auto text-blue-500" /></div>}

                        {!loading && customers.length === 0 && customerSearch.length >= 3 && (
                            <div className="text-center py-8 text-gray-400">
                                <Icons.UserX className="mx-auto w-8 h-8 mb-2 opacity-50" />
                                <p>No se encontraron clientes</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Order Selection & Payment Distribution */}
                {step === 2 && (
                    <div className="flex flex-col h-[70vh]">
                        {/* Header Info */}
                        <div className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center shrink-0">
                            <div>
                                <div className="text-xs text-gray-500 uppercase font-bold">Cliente</div>
                                <div className="font-bold text-gray-800">{customerSearch}</div>
                            </div>
                            <button
                                onClick={() => setStep(1)}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center font-medium bg-white px-3 py-1.5 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
                            >
                                <Icons.ArrowLeft className="w-3 h-3 mr-1" />
                                Cambiar
                            </button>
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                            {/* Left Pane: Order Selection */}
                            <div className="w-full md:w-7/12 p-4 overflow-y-auto border-r border-gray-100 custom-scrollbar">
                                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                                    <Icons.ShoppingCart className="w-4 h-4 mr-2" />
                                    Pedidos Pendientes
                                </h4>
                                <Table size="sm" hover className="mb-0">
                                    <thead className="bg-white sticky top-0 z-10">
                                        <tr>
                                            <th className="w-8 sticky top-0 bg-white">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOrders.size === pendingOrders.length && pendingOrders.length > 0}
                                                    onChange={() => {
                                                        if (selectedOrders.size === pendingOrders.length) {
                                                            setSelectedOrders(new Map());
                                                        } else {
                                                            const newMap = new Map();
                                                            pendingOrders.forEach(o => newMap.set(o.id, parseFloat(o.balance)));
                                                            setSelectedOrders(newMap);
                                                        }
                                                    }}
                                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                />
                                            </th>
                                            <th className="text-xs font-bold text-gray-500 uppercase sticky top-0 bg-white">Pedido</th>
                                            <th className="text-xs font-bold text-gray-500 uppercase text-right sticky top-0 bg-white">Saldo</th>
                                            <th className="text-xs font-bold text-gray-500 uppercase text-right sticky top-0 bg-white">Aplicar</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {pendingOrders.map((order) => {
                                            const isSelected = selectedOrders.has(order.id);
                                            const isInternal = order.is_internal === 1 || order.is_internal === true;
                                            return (
                                                <tr key={order.id} className={`${isSelected ? 'bg-blue-50/30' : ''} ${isInternal ? 'bg-amber-50/20' : ''}`}>
                                                    <td className="align-middle">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleToggleOrder(order)}
                                                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <div className="flex items-center gap-1.5">
                                                            {isInternal && (
                                                                <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-1.5 py-0.5 rounded border border-amber-200 shrink-0">
                                                                    INT
                                                                </span>
                                                            )}
                                                            <span className={`font-bold text-sm ${isInternal ? 'text-amber-800' : 'text-gray-800'}`}>
                                                                {order.order_number}
                                                            </span>
                                                        </div>
                                                        <div className="text-[10px] text-gray-500">
                                                            {formatDate(order.date || order.created_at)}
                                                            {isInternal && order.parent_order_number && (
                                                                <span className="ml-1 text-amber-600">
                                                                    ← {order.parent_order_number}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="align-middle text-right text-xs font-medium text-gray-600">
                                                        {formatCurrencyCOP(order.balance)}
                                                    </td>
                                                    <td className="align-middle text-right">
                                                        {isSelected ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                value={selectedOrders.get(order.id)}
                                                                onChange={(e) => handleUpdatePaymentAmount(order.id, e.target.value)}
                                                                className="text-right font-bold w-full max-w-[120px] ml-auto border-blue-200 focus:border-blue-500 focus:ring-blue-200"
                                                            />
                                                        ) : (
                                                            <span className="text-gray-300">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-gray-50 border-t border-gray-200 sticky bottom-0">
                                        <tr>
                                            <td colSpan="3" className="text-right text-xs font-bold text-gray-600 py-3">Total a Pagar:</td>
                                            <td className="text-right text-sm font-black text-blue-700 py-3 px-2">
                                                {formatCurrencyCOP(calculateTotalPayment())}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            {/* Right Pane: Payment Details */}
                            <div className="w-full md:w-5/12 p-4 bg-gray-50/50 overflow-y-auto custom-scrollbar">
                                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                                    <Icons.CreditCard className="w-4 h-4 mr-2" />
                                    Detalle del Pago
                                </h4>

                                <div className="space-y-4">
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                        <FormGroup>
                                            <Label className="text-xs font-bold text-gray-500 uppercase mb-1">Método de Pago</Label>
                                            <div className="relative">
                                                <Input
                                                    type="select"
                                                    value={siigoPaymentId}
                                                    onChange={(e) => {
                                                        const newId = e.target.value;
                                                        setSiigoPaymentId(newId);
                                                        if (newId === '7476') setPaymentMethod('transferencia');
                                                        else if (newId === '5671') setPaymentMethod('efectivo');
                                                        else if (newId === '11136') setPaymentMethod('bold');
                                                        else setPaymentMethod('otros');
                                                    }}
                                                    className="pl-3 pr-8 py-2 bg-gray-50 border-gray-200 focus:ring-emerald-500 focus:border-emerald-500"
                                                >
                                                    <option value="7476">Consignación Bancaria / Transferencia (7476)</option>
                                                    <option value="5671">Efectivo (5671)</option>
                                                    <option value="11136">Bold / Datáfono (11136)</option>
                                                </Input>
                                                <Icons.ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                                            </div>
                                        </FormGroup>
                                    </div>

                                    {/* Comprobante de Pago (Upload + Paste + Drag) */}
                                    <div
                                        className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
                                        onPaste={handlePaste}
                                        tabIndex={0}
                                    >
                                        <Label className="text-xs font-bold text-gray-500 uppercase mb-2 block">
                                            Comprobante de Pago
                                        </Label>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-center w-full">
                                                <label
                                                    htmlFor="multi-rc-upload"
                                                    className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDragging
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                                                        }`}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}
                                                >
                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                        <Icons.UploadCloud className={`w-8 h-8 mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                                                        <p className="text-xs text-gray-500"><span className="font-semibold">Clic para subir</span>, arrastrar o <span className="font-semibold">Ctrl+V</span></p>
                                                        <p className="text-[10px] text-gray-400">IMG, PDF (Max. 5MB)</p>
                                                    </div>
                                                    <input
                                                        id="multi-rc-upload"
                                                        type="file"
                                                        className="hidden"
                                                        multiple
                                                        accept="image/*,application/pdf"
                                                        onChange={handleFileChange}
                                                    />
                                                </label>
                                            </div>

                                            {/* File Preview List */}
                                            {files.length > 0 && (
                                                <div className="space-y-2">
                                                    {files.map((file, index) => (
                                                        <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-100">
                                                            <div className="flex items-center overflow-hidden">
                                                                <Icons.FileText className="w-4 h-4 text-blue-500 mr-2 shrink-0" />
                                                                <span className="text-xs text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => removeFile(index)}
                                                                className="text-gray-400 hover:text-red-500 p-1"
                                                            >
                                                                <Icons.X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {(paymentMethod === 'transferencia' || paymentMethod === 'bold') && (
                                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                                        <div className="flex justify-between items-center mb-3">
                                            <Label className="text-xs font-bold text-indigo-700 uppercase flex items-center mb-0">
                                                <Icons.Link className="w-3 h-3 mr-1.5" />
                                                Conciliación Bancaria
                                            </Label>
                                            {linkedMovements.length > 0 && (
                                                <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-emerald-200 flex items-center shadow-sm">
                                                    <Icons.CheckCircle2 className="w-3 h-3 mr-1" /> {linkedMovements.length}
                                                </span>
                                            )}
                                        </div>

                                        {/* Linked List */}
                                        {linkedMovements.length > 0 && (
                                            <div className="space-y-2 mb-4">
                                                {linkedMovements.map((lm) => (
                                                    <div key={lm.id} className="bg-white p-3 rounded-lg border border-emerald-200 shadow-sm flex justify-between items-center group relative">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="bg-emerald-100 p-2 rounded-full shrink-0">
                                                                <Icons.Check className="w-4 h-4 text-emerald-700" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-bold text-gray-800">
                                                                    {formatCurrencyCOP(lm.valor)}
                                                                </div>
                                                                <div className="text-[10px] text-gray-500 truncate max-w-[120px]" title={lm.descripcion}>
                                                                    {lm.descripcion}
                                                                </div>
                                                                <div className="text-[10px] text-gray-400">
                                                                    {formatDate(lm.fecha)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleUnlinkMovement(lm.id)}
                                                            className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                            title="Desvincular"
                                                        >
                                                            <Icons.X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                                <div className="flex justify-between items-center pt-2 px-1 border-t border-indigo-200 border-dashed">
                                                    <span className="text-xs font-medium text-indigo-800">Total Vinculado:</span>
                                                    <span className="text-sm font-bold text-emerald-700">
                                                        {formatCurrencyCOP(linkedMovements.reduce((sum, m) => sum + Number(m.valor), 0))}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Search Input */}
                                        <div className="relative group">
                                            <Icons.Search className="absolute left-3 top-3 text-gray-400 w-3.5 h-3.5" />
                                            <input
                                                type="text"
                                                placeholder="Buscar movimiento (valor, fecha)..."
                                                value={movementSearchQuery}
                                                onChange={(e) => handleSearchMovements(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2 text-sm border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm"
                                            />
                                            {loadingMovements && (
                                                <Icons.Loader className="absolute right-3 top-3 w-3.5 h-3.5 animate-spin text-gray-400" />
                                            )}

                                            {/* Search Results - inside relative container for correct positioning */}
                                            {foundMovements.length > 0 && (
                                                <div className="mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg absolute z-50 w-full left-0 top-full">
                                                    {foundMovements.map(mov => (
                                                        <div
                                                            key={mov.id}
                                                            onClick={() => handleLinkMovement(mov)}
                                                            className="p-2 border-b last:border-0 hover:bg-indigo-50 cursor-pointer transition-colors"
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span className="font-bold text-sm text-gray-800">
                                                                    {formatCurrencyCOP(mov.valor)}
                                                                </span>
                                                                <span className="text-[10px] text-gray-400">{formatDate(mov.fecha)}</span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate" title={mov.descripcion}>
                                                                {mov.descripcion}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                    <FormGroup className="mb-0">
                                        <Label className="text-xs font-bold text-gray-500 uppercase mb-1">Notas (Opcional)</Label>
                                        <Input
                                            type="textarea"
                                            rows="3"
                                            placeholder="Observaciones adicionales..."
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            className="bg-gray-50 border-gray-200 text-sm focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>

            <ModalFooter className="bg-gray-50 border-t border-gray-200">
                {step === 2 && (
                    <>
                        <Button color="white" className="border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setStep(1)} disabled={loading}>
                            Atrás
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleCreateRC}
                            disabled={loading || selectedOrders.size === 0}
                            className="bg-blue-600 border-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200"
                        >
                            {loading ? (
                                <>
                                    <Icons.Loader className="animate-spin mr-2 inline" size={16} />
                                    Creando...
                                </>
                            ) : (
                                <>
                                    <Icons.CheckCircle className="mr-2 inline" size={16} />
                                    Crear RC ({selectedOrders.size} pedidos)
                                </>
                            )}
                        </Button>
                    </>
                )}
                {step === 1 && (
                    <Button color="white" className="border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={toggle}>
                        Cancelar
                    </Button>
                )}
            </ModalFooter>
        </Modal >
    );
};

export default MultiInvoiceRCDialog;

